import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
    AlertTriangle, ShieldCheck, FileWarning, BookOpen,
    Folder, ListChecks, Activity, Compass, BarChart3,
    Plus, Search, Briefcase, Landmark, MapPinned, Wrench, CalendarClock,
    ClipboardList, Settings,
} from "lucide-react";
import GeographyTypesMaster from "./GeographyTypesMaster";
import CadenceEventsMaster from "../cadence/CadenceEventsMaster";
import CadenceThresholdsMaster from "../cadence/CadenceThresholdsMaster";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import StatusSwitch from "../common/StatusSwitch";
import { usePermission } from "../../contexts/PermissionContext";
import { RBAC_ACTIVITY as RISK_RBAC_ACTIVITY } from "../risk/rbacMock";

// Risk masters
import RiskCategoriesTab from "../risk/RiskCategoriesTab";
import RiskCausesTab from "../risk/RiskCausesTab";
import RiskImpactsTab from "../risk/RiskImpactsTab";
import RiskDriversTab from "../risk/RiskDriversTab";
import RiskKrisTab from "../risk/RiskKrisTab";

// Internal Controls masters
import ControlCategoriesTab from "../internalControls/ControlCategoriesTab";

// Incident Reporting masters
import { MasterCRUD as IncidentMasterCRUD, type MasterKey as IncidentMasterKey } from "../incidenceReporting/IncidentMasters";

// Governance Masters — internal sub-nav owns its own Law Library / Contracts /
// Jurisdictions selector; surfaced as a single leaf entry under the Governance
// group for now.
import GovernanceMasters from "../governance/GovernanceMasters";

// Audit masters — Populations (entity-scoped CRUD, gated on ADT_PROGRAMS).
import PopulationMaster from "../organisation/PopulationMaster";

// Organisation settings — per-entity display styling (singleton, gated on ORG_ENTITY_PROFILE).
import EntitySettings from "../organisation/EntitySettings";


// =============================================================================
// Risk leaf wrapper
// =============================================================================
// The five Risk leaf components (Categories / Causes / Impacts / Drivers /
// Kris) are NOT self-contained — they expect an outer toolbar (page size,
// search, status filter, "Create" trigger). RiskMastersTab used to provide
// that. We rebuild the same toolbar here so the leaves can be hosted as
// individual entries in the Org Masters left rail.

type RiskLeafKind = "categories" | "causes" | "impacts" | "drivers" | "kris";

const RISK_LEAF_PLACEHOLDER: Record<RiskLeafKind, string> = {
    categories: "Search categories…",
    causes:     "Search causes…",
    impacts:    "Search impacts…",
    drivers:    "Search drivers…",
    kris:       "Search KRIs…",
};

const RiskLeafWrapper: React.FC<{ kind: RiskLeafKind }> = ({ kind }) => {
    const [createTrigger, setCreateTrigger] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ACTIVE");

    // Toolbar mirrors the inner tab's RBAC gate so unauthorised users don't
    // see "Show / Search / Create" floating above the NotAuthorised pane.
    const viewPerm = usePermission(RISK_RBAC_ACTIVITY, "view");
    const modifyPerm = usePermission(RISK_RBAC_ACTIVITY, "view+modify");
    const showToolbar = viewPerm.loading || viewPerm.allowed;
    const canCreate = modifyPerm.allowed;

    const shared = { createTrigger, pageSize, onPageSizeChange: setPageSize, searchTerm, statusFilter };
    const leaf =
        kind === "categories" ? <RiskCategoriesTab {...shared} /> :
        kind === "causes"     ? <RiskCausesTab     {...shared} /> :
        kind === "impacts"    ? <RiskImpactsTab    {...shared} /> :
        kind === "drivers"    ? <RiskDriversTab    {...shared} /> :
                                <RiskKrisTab        {...shared} />;

    return (
        <div className="flex flex-col flex-1 min-w-0 h-full">
            {showToolbar && (
                <div className="netbody-header">
                    <StatusSwitch
                        value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                        onChange={next => setStatusFilter(next)}
                        noun="records"
                        testId="SEL_orgmasters_risk_status_filter"
                    />
                    <div className="flex items-center gap-2 ml-auto">
                        <FormControl.Input
                            type="text"
                            placeholder={RISK_LEAF_PLACEHOLDER[kind]}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            leadingIcon={<Search size={14} />}
                            className="filter-search"
                            data-testid="INP_orgmasters_risk_search"
                        />
                        {canCreate && (
                            <Button variant="fill" icon={Plus} data-testid="BTN_orgmasters_risk_create" onClick={() => setCreateTrigger(n => n + 1)}>Create</Button>
                        )}
                    </div>
                </div>
            )}
            <div className="netbody-content">{leaf}</div>
        </div>
    );
};

// =============================================================================
// Master catalogue
// =============================================================================
// Single source of truth for the left-rail menu. Each leaf = {key, label,
// group, icon, render}. Components reused as-is from their original module
// screens — no copy-paste, no behavioural drift.

type MasterDef = {
    key: string;
    label: string;
    group: string;
    icon: React.ElementType;
    render: () => React.ReactNode;
};

const MASTERS: MasterDef[] = [
    // ─── Risk ─────────────────────────────────────────────────────────────
    { key: "risk-categories", label: "Risk Categories",       group: "Risk", icon: Folder,        render: () => <RiskLeafWrapper kind="categories" /> },
    { key: "risk-causes",     label: "Risk Causes",           group: "Risk", icon: AlertTriangle, render: () => <RiskLeafWrapper kind="causes" /> },
    { key: "risk-impacts",    label: "Risk Impacts",          group: "Risk", icon: AlertTriangle, render: () => <RiskLeafWrapper kind="impacts" /> },
    { key: "risk-drivers",    label: "Risk Drivers",          group: "Risk", icon: Compass,       render: () => <RiskLeafWrapper kind="drivers" /> },
    { key: "risk-kris",       label: "Key Risk Indicators",   group: "Risk", icon: BarChart3,     render: () => <RiskLeafWrapper kind="kris" /> },

    // ─── Internal Controls ────────────────────────────────────────────────
    { key: "control-categories", label: "Control Categories", group: "Internal Controls", icon: ShieldCheck, render: () => <ControlCategoriesTab /> },

    // ─── Incident Reporting ───────────────────────────────────────────────
    { key: "irg-incidents",  label: "Incident Types",   group: "Incident Reporting", icon: FileWarning,   render: () => <IncidentMasterCRUD title="Type of Incidents"  masterKey={"incidents"  as IncidentMasterKey} /> },
    { key: "irg-risks",      label: "Risk Types",       group: "Incident Reporting", icon: AlertTriangle, render: () => <IncidentMasterCRUD title="Type of Risks"      masterKey={"risks"      as IncidentMasterKey} /> },
    { key: "irg-processes",  label: "Process Types",    group: "Incident Reporting", icon: Activity,      render: () => <IncidentMasterCRUD title="Type of Process"    masterKey={"processes"  as IncidentMasterKey} /> },
    { key: "irg-categories", label: "Event Categories", group: "Incident Reporting", icon: ListChecks,    render: () => <IncidentMasterCRUD title="Category (Event)"   masterKey={"categories" as IncidentMasterKey} /> },

    // ─── Governance ───────────────────────────────────────────────────────
    // GovernanceMasters supports a `subTab` prop that suppresses its internal
    // left-rail so each governance master appears as a flat leaf in the
    // consolidated rail.
    { key: "gov-laws",          label: "Law Library",   group: "Governance", icon: BookOpen,  render: () => <GovernanceMasters subTab="laws" /> },
    { key: "gov-parties",       label: "Parties",       group: "Governance", icon: Briefcase, render: () => <GovernanceMasters subTab="contracts" /> },
    { key: "gov-jurisdictions", label: "Jurisdictions", group: "Governance", icon: Landmark,  render: () => <GovernanceMasters subTab="jurisdictions" /> },
    { key: "gov-capa-impact-types", label: "CAPA Impact Types", group: "Governance", icon: Wrench, render: () => <GovernanceMasters subTab="capa_impact_types" /> },

    // ─── Organisation ─────────────────────────────────────────────────────
    // Masters scoped to the Organisation module itself (consumed by the
    // Geographies tab).
    { key: "org-geography-types", label: "Geography Types", group: "Organisation", icon: MapPinned, render: () => <GeographyTypesMaster /> },
    { key: "org-settings", label: "Settings", group: "Organisation", icon: Settings, render: () => <EntitySettings /> },

    // ─── Cadence ───────────────────────────
    { key: "cadence-events", label: "Cadence Events", group: "Cadence", icon: CalendarClock, render: () => <CadenceEventsMaster /> },
    { key: "cadence-thresholds", label: "Cadence Thresholds", group: "Cadence", icon: Activity, render: () => <CadenceThresholdsMaster /> },

    // ─── Audit ────────────────────────────────────────────────────────────
    // Population labels consumed by GRC audit procedures during scope
    // definition. Self-contained CRUD, gated on the ADT_PROGRAMS activity.
    { key: "audit-populations", label: "Populations", group: "Audit", icon: ClipboardList, render: () => <PopulationMaster /> },

];

const GROUP_ORDER = ["Organisation", "Cadence", "Audit", "Risk", "Internal Controls", "Incident Reporting", "Governance"];

// Lightweight catalogue (key + label + group, no render fn) so the global
// header search can index every Masters section without pulling in the heavy
// leaf component imports. Single source of truth — derived from MASTERS.
export const MASTER_SECTIONS: Array<{ key: string; label: string; group: string }> =
    MASTERS.map(({ key, label, group }) => ({ key, label, group }));

// =============================================================================
// OrgMasters — left-rail catalogue + right-pane CRUD
// =============================================================================
const OrgMasters: React.FC = () => {
    // Honour an initial ?section=<masterKey> deep-link (e.g. the legacy
    // ?tab=population redirect lands on Masters → Populations) — fall back to
    // the first master when the section is absent or unknown.
    const [params, setParams] = useSearchParams();
    const initialKey = MASTERS.find(m => m.key === params.get("section"))?.key ?? MASTERS[0].key;
    const [activeKey, setActiveKey] = useState<string>(initialKey);

    // Persist the selected master into the URL (?section=<key>) so a reload —
    // or a shared link — restores the same rail selection instead of falling
    // back to MASTERS[0] (Risk Categories). Keeps the existing ?tab=masters
    // param intact and doesn't add a history entry per click.
    const selectMaster = (key: string) => {
        setActiveKey(key);
        setParams(prev => {
            const next = new URLSearchParams(prev);
            next.set("section", key);
            return next;
        }, { replace: true });
    };

    // React to ?section=<masterKey> changes that arrive after mount (e.g. the
    // global header search navigates here while Masters is already open — the
    // URL changes but this component does not remount).
    const sectionParam = params.get("section");
    useEffect(() => {
        const match = MASTERS.find(m => m.key === sectionParam);
        if (match) setActiveKey(match.key);
    }, [sectionParam]);

    const groups = useMemo(() => {
        const byGroup: Record<string, MasterDef[]> = {};
        for (const m of MASTERS) (byGroup[m.group] = byGroup[m.group] || []).push(m);
        const ordered = [...GROUP_ORDER, ...Object.keys(byGroup).filter(g => !GROUP_ORDER.includes(g)).sort()];
        return ordered.filter(g => byGroup[g]).map(g => ({ name: g, items: byGroup[g] }));
    }, []);

    const active = MASTERS.find(m => m.key === activeKey) || MASTERS[0];

    return (
        <div className="flex h-full overflow-hidden">
            {/* Left rail: grouped master catalogue.
                Fixed-zone layout — the card is bound to the viewport and only the
                grouped <nav> list scrolls, so scrolling the right pane never drags
                this rail up (and vice-versa). All groups live inside ONE <nav> so
                the whole catalogue scrolls as a single region. */}
            <div className="netbody-card side-nav-card side-nav-card--fixed">
                <nav aria-label="Masters catalogue">
                    {groups.map(g => (
                        <div key={g.name}>
                            <div className="side-nav-card-heading">{g.name}</div>
                            {g.items.map(m => (
                                <button
                                    key={m.key}
                                    data-testid={`NAV_orgmasters_${m.key}`}
                                    onClick={() => selectMaster(m.key)}
                                    className={`mt-2 side-panel-menu-item${activeKey === m.key ? " side-panel-menu-item--active" : ""}`}
                                >
                                    <m.icon size={14} />
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    ))}
                </nav>
            </div>

            {/* Right pane: the selected master's CRUD screen. `key` forces
                remount on switch so leaf components start clean.
                Symmetric fixed-zone — only the body scrolls, the card stays
                bound to the viewport. */}
            <div className="netbody-card side-content-card side-content-card--fixed flex flex-col flex-1 min-w-0">
                <React.Fragment key={active.key}>
                    {active.render()}
                </React.Fragment>
            </div>
        </div>
    );
};

export default OrgMasters;
